/**
 * Module block management for the docx-editor.dev integration.
 *
 * Strategy:
 *   1. `initModuleBlock(view)` finds the "Thank you for…" / "Yours
 *      sincerely…" anchor paragraphs in the loaded template, deletes
 *      whatever was between them, and records that empty range as the
 *      "module block" in plugin state.
 *   2. `setModuleValues(view, modules)` rebuilds the module block with
 *      ONLY the modules that have non-empty content — one bold heading
 *      paragraph + content paragraph + spacer per filled module. Called
 *      whenever the user's textareas or radio selections change.
 *
 * Why rebuild instead of in-place replace: production templates don't
 * author `{{module_name}}` tokens for every module (only the case-context
 * fields like `{{CLAIMANT_NAME}}`). The previous TipTap Split used an HTML
 * splice between the same two anchors to render module slots; this is the
 * docx-editor analogue.
 *
 * Trade-off: rebuild loses the cursor in the editor on every textarea
 * keystroke (debounced 250ms). Acceptable for a left-pane-driven flow;
 * worth revisiting if direct editor editing becomes the primary use case.
 */
import {
  Fragment,
  type Node as PMNode,
  type Schema,
  Slice,
} from "prosemirror-model";
import { type EditorState, Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";

import { buildInlineNodes, firstRunMarks, visualParaAttrs } from "./para-clone";
import { stripHtmlToText } from "./utils";

export type SlotRange = { from: number; to: number };

export type ModuleContent = {
  /** DB module name — used as the canonical key (lowercased internally). */
  name: string;
  /** Human-readable heading shown bold above the content. */
  label: string;
  /** Plain-text content; `\n` becomes a hardBreak. */
  value: string;
};

type SlotState = {
  /** Per-module slot ranges (typically one each, but multi-occurrence is
   *  preserved in case a future template references the same module twice). */
  ranges: Map<string, SlotRange[]>;
  /** Module currently being edited from the left pane — gets a soft highlight. */
  activeName: string | null;
  /** Bounds of the dynamic module-block area within the doc. */
  moduleBlock: SlotRange | null;
};

type SlotMeta =
  | {
      type: "rebuild";
      moduleBlock: SlotRange;
      ranges: Map<string, SlotRange[]>;
    }
  | { type: "setActive"; name: string | null };

const ACTIVE_SLOT_STYLE =
  "background-color: rgba(250, 204, 21, 0.22); border-radius: 2px;";

export const slotPluginKey = new PluginKey<SlotState>("moduleSlot");

/** ProseMirror plugin that tracks the module block bounds and per-module
 *  content ranges, mapping them through every transaction so positions
 *  stay valid after unrelated edits. */
export const slotPlugin = new Plugin<SlotState>({
  key: slotPluginKey,
  state: {
    init: () => ({ ranges: new Map(), activeName: null, moduleBlock: null }),
    apply: (tr, prev) => {
      const ranges = new Map<string, SlotRange[]>();
      for (const [name, list] of prev.ranges) {
        const mapped: SlotRange[] = [];
        for (const range of list) {
          const from = tr.mapping.map(range.from, 1);
          const to = tr.mapping.map(range.to, -1);
          if (to >= from) mapped.push({ from, to });
        }
        if (mapped.length > 0) ranges.set(name, mapped);
      }
      let moduleBlock = prev.moduleBlock
        ? {
            from: tr.mapping.map(prev.moduleBlock.from, 1),
            to: tr.mapping.map(prev.moduleBlock.to, -1),
          }
        : null;
      let activeName = prev.activeName;

      const meta = tr.getMeta(slotPluginKey) as SlotMeta | undefined;
      if (meta?.type === "rebuild") {
        moduleBlock = meta.moduleBlock;
        ranges.clear();
        for (const [n, list] of meta.ranges) {
          ranges.set(n, list);
        }
      } else if (meta?.type === "setActive") {
        activeName = meta.name;
      }
      return { ranges, activeName, moduleBlock };
    },
  },
  props: {
    decorations(state) {
      const slot = slotPluginKey.getState(state);
      if (!slot?.activeName) return DecorationSet.empty;
      const ranges = slot.ranges.get(slot.activeName);
      if (!ranges || ranges.length === 0) return DecorationSet.empty;
      const decos = ranges.map((r) =>
        Decoration.inline(r.from, r.to, { style: ACTIVE_SLOT_STYLE })
      );
      return DecorationSet.create(state.doc, decos);
    },
  },
});

// ── Anchor + bounds setup ────────────────────────────────────────────────

/**
 * Locate the "Thank you for…" and "Yours sincerely…" paragraphs in the
 * doc, delete everything between them, and record an empty `moduleBlock`
 * range in plugin state at that position. Subsequent calls to
 * `setModuleValues` fill that range with rendered modules. Idempotent
 * via state.moduleBlock — calling twice is a no-op.
 *
 * @returns true on success; false if the editor's schema lacks a
 *   `paragraph` node (defensive).
 */
export const initModuleBlock = (view: EditorView): boolean => {
  const state = slotPluginKey.getState(view.state);
  if (state?.moduleBlock) return true; // already initialised
  if (!view.state.schema.nodes.paragraph) return false;

  let thankYouEnd: number | null = null;
  let signatureStart: number | null = null;
  view.state.doc.descendants((node, pos) => {
    if (node.type.name !== "paragraph") return true;
    const text = node.textContent;
    if (thankYouEnd === null && /(thank you for|i write regarding)/i.test(text)) {
      thankYouEnd = pos + node.nodeSize;
    }
    if (signatureStart === null && /yours\s+(sincerely|faithfully)/i.test(text)) {
      signatureStart = pos;
    }
    return false;
  });

  let start: number;
  let end: number;
  if (
    thankYouEnd !== null &&
    signatureStart !== null &&
    signatureStart > thankYouEnd
  ) {
    start = thankYouEnd;
    end = signatureStart;
  } else if (thankYouEnd !== null) {
    start = thankYouEnd;
    end = thankYouEnd;
  } else {
    start = view.state.doc.content.size;
    end = view.state.doc.content.size;
  }

  const tr = view.state.tr.delete(start, end);
  tr.setMeta(slotPluginKey, {
    type: "rebuild",
    moduleBlock: { from: start, to: start },
    ranges: new Map(),
  });
  view.dispatch(tr);
  return true;
};

// ── Inline content building ──────────────────────────────────────────────

/** Build an inline Fragment from a plain-text value, converting `\n` to
 *  `hardBreak` nodes so soft line breaks survive the .docx round-trip. */
export const buildInlineFragment = (schema: Schema, value: string): Fragment => {
  if (!value) return Fragment.empty;
  const hardBreak = schema.nodes.hardBreak;
  const lines = value.split("\n");
  const nodes: PMNode[] = [];
  lines.forEach((line, i) => {
    if (line) nodes.push(schema.text(line));
    if (i < lines.length - 1 && hardBreak) {
      nodes.push(hardBreak.create());
    } else if (i < lines.length - 1) {
      nodes.push(schema.text(" "));
    }
  });
  return Fragment.from(nodes);
};

// ── Module block rebuild ─────────────────────────────────────────────────

/**
 * Replace the module block range with paragraphs rendered from `modules`.
 * Filters to only those with non-empty values — empty modules are absent
 * from the doc entirely. Each rendered module = bold heading paragraph +
 * content paragraph. An empty paragraph spacer is inserted between
 * adjacent modules for visual breathing room.
 *
 * @returns true if the doc was updated; false if `initModuleBlock` hasn't
 *   been called yet or the schema can't be built against.
 */
export const setModuleValues = (
  view: EditorView,
  modules: ModuleContent[]
): boolean => {
  const state = slotPluginKey.getState(view.state);
  if (!state?.moduleBlock) return false;

  const schema = view.state.schema;
  const paragraphType = schema.nodes.paragraph;
  if (!paragraphType) return false;
  const strongMark = schema.marks.strong ?? schema.marks.bold;

  const filled = modules.filter((m) => m.value.trim().length > 0);
  const headingMarks = strongMark ? [strongMark.create()] : undefined;

  const nodes: PMNode[] = [];
  const newRanges = new Map<string, SlotRange[]>();
  // Cursor walks the post-replace doc; everything starts at the block's
  // current `from` position (which has been mapped through any prior tx).
  let cursor = state.moduleBlock.from;

  // Leading spacer before the first module so it doesn't butt up against
  // the "Thank you for…" anchor paragraph.
  if (filled.length > 0) {
    const leadSpacer = paragraphType.create(null, Fragment.empty);
    nodes.push(leadSpacer);
    cursor += leadSpacer.nodeSize;
  }

  for (let i = 0; i < filled.length; i++) {
    const m = filled[i];

    // Heading paragraph
    const headingTextNode = m.label ? schema.text(m.label, headingMarks) : null;
    const headingPara = paragraphType.create(
      null,
      headingTextNode ? Fragment.from(headingTextNode) : Fragment.empty
    );
    nodes.push(headingPara);
    cursor += headingPara.nodeSize;

    // Content paragraph — record the inline content range for mirror-back.
    const contentFragment = buildInlineFragment(schema, m.value);
    const contentPara = paragraphType.create(null, contentFragment);
    const contentStart = cursor + 1; // skip open token
    const contentEnd = contentStart + contentFragment.size;
    newRanges.set(m.name.toLowerCase(), [{ from: contentStart, to: contentEnd }]);
    nodes.push(contentPara);
    cursor += contentPara.nodeSize;

    // Spacer after every module's content paragraph — including the
    // last one, so the "Yours faithfully," anchor below has breathing
    // room from the final module.
    const spacer = paragraphType.create(null, Fragment.empty);
    nodes.push(spacer);
    cursor += spacer.nodeSize;
  }

  const fragment = Fragment.from(nodes);
  const slice = new Slice(fragment, 0, 0);
  const newBlockEnd = state.moduleBlock.from + slice.content.size;

  try {
    const tr = view.state.tr.replace(
      state.moduleBlock.from,
      state.moduleBlock.to,
      slice
    );
    tr.setMeta(slotPluginKey, {
      type: "rebuild",
      moduleBlock: { from: state.moduleBlock.from, to: newBlockEnd },
      ranges: newRanges,
    });
    view.dispatch(tr);
    return true;
  } catch (err) {
    console.warn("[slots] setModuleValues failed:", err);
    return false;
  }
};

// ── Active slot decoration ───────────────────────────────────────────────

/** Set (or clear) the currently-active slot — drives the inline
 *  highlight decoration in the editor. */
export const setActiveSlot = (view: EditorView, name: string | null) => {
  const tr = view.state.tr.setMeta(slotPluginKey, {
    type: "setActive",
    name: name == null ? null : name.toLowerCase(),
  });
  view.dispatch(tr);
};

// ── Read-back helpers ────────────────────────────────────────────────────

/** Names of modules currently rendered in the doc (have a tracked range). */
export const listTrackedSlots = (view: EditorView): Set<string> => {
  const state = slotPluginKey.getState(view.state);
  return new Set(state?.ranges.keys() ?? []);
};

/** Read the current text content of every tracked slot, converting
 *  `hardBreak` nodes back to `\n`. Used by the mirror-back path so direct
 *  edits in the editor propagate back into the textareas. */
export const readTrackedSlots = (view: EditorView): Map<string, string> => {
  const state = slotPluginKey.getState(view.state);
  if (!state) return new Map();
  const out = new Map<string, string>();
  const leafText = (node: { type: { name: string } }) =>
    node.type.name === "hardBreak" ? "\n" : "";
  for (const [name, list] of state.ranges) {
    if (list.length === 0) continue;
    const first = list[0];
    const text = view.state.doc.textBetween(
      first.from,
      first.to,
      undefined,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      leafText as any
    );
    out.set(name, text);
  }
  return out;
};

/** PM position for a module's slot, or the module block start if the
 *  module isn't currently rendered. Used to scroll the editor when the
 *  textarea gains focus. */
export const getSlotPosition = (view: EditorView, name: string): number | null => {
  const key = name.toLowerCase();
  const state = slotPluginKey.getState(view.state);
  const tracked = state?.ranges.get(key);
  if (tracked && tracked.length > 0) return tracked[0].from;
  if (state?.moduleBlock) return state.moduleBlock.from;
  return null;
};

/** Module block bounds, exposed for callers that need to know where the
 *  dynamic content area lives (currently unused but handy for debugging). */
export const getModuleBlock = (view: EditorView): SlotRange | null => {
  const state = slotPluginKey.getState(view.state);
  return state?.moduleBlock ?? null;
};

// ── Drop plugin ──────────────────────────────────────────────────────────

/** Walk the PM doc to find the open-token position of the paragraph node
 *  whose `paraId` matches. Callers add `1 + before.length` to compute
 *  inline positions inside the paragraph. Returns null if not found. */
export const findParagraphStart = (
  view: EditorView,
  paraId: string
): number | null => {
  let found: number | null = null;
  view.state.doc.descendants((node, pos) => {
    if (found !== null) return false;
    if (
      (node.type.name === "paragraph" || node.type.name === "doc_paragraph") &&
      node.attrs?.paraId === paraId
    ) {
      found = pos;
      return false;
    }
    return true;
  });
  return found;
};

/**
 * Backstop drop handler for text/html and text/plain payloads coming from
 * the AI snippet cards' DraggableSnippetBody. docx-editor wires its own
 * drop logic for embedded images; this plugin returns `true` from
 * handleDrop only when the dataTransfer contains text — letting image
 * drops fall through to docx-editor untouched.
 */
/**
 * Marks to apply to text inserted at `pos` so it blends with the surrounding
 * template body. Walks up to the nearest textblock and reads its first text
 * run's marks (font / size / weight); falls back to the marks active at the
 * cursor when the parent has no text yet. Without this the inserted text falls
 * back to the schema default (Calibri 11pt / "Normal text") while the rest of
 * the report uses the template's chosen font — QA reported this as a jarring
 * two-style paragraph on the Dr Sam Jones template.
 */
const donorMarksAt = (
  state: EditorState,
  pos: number
): readonly import("prosemirror-model").Mark[] => {
  const $pos = state.doc.resolve(pos);
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const block = $pos.node(depth);
    if (block.isTextblock) {
      const marks = firstRunMarks(block);
      if (marks.length > 0) return marks;
      break;
    }
  }
  return $pos.marks();
};

export const dropPlugin = new Plugin({
  props: {
    handleDrop(view, event) {
      const dt = event.dataTransfer;
      if (!dt) return false;
      const html = dt.getData("text/html");
      const text = dt.getData("text/plain");
      if (!html && !text) return false;

      // Resolve the drop position from event coordinates.
      const coords = view.posAtCoords({
        left: event.clientX,
        top: event.clientY,
      });
      if (!coords) return false;

      const schema = view.state.schema;
      // Build inline content: split on \n, use hardBreak between lines.
      // text/plain is the source of truth here because docx-editor's
      // schema is OOXML and arbitrary text/html may not parse cleanly.
      // When text/plain is missing, fall back to DOMParser-based
      // text extraction (a regex strip is brittle — malformed tags slip
      // through and CodeQL flags it as incomplete sanitization, even
      // though the result is only used as ProseMirror text content).
      const source = text || stripHtmlToText(html);
      if (!source.trim()) return false;

      const donorMarks = donorMarksAt(view.state, coords.pos);
      const nodes: PMNode[] = buildInlineNodes(schema, source, donorMarks);
      if (nodes.length === 0) return false;

      const tr = view.state.tr.insert(coords.pos, Fragment.from(nodes));
      view.dispatch(tr);
      event.preventDefault();
      return true;
    },
  },
});

/** Visual attrs of the paragraph enclosing `pos` — applied to synthesised
 *  paragraphs so they inherit the destination's style (see visualParaAttrs). */
const donorParaAttrs = (
  state: EditorState,
  pos: number
): Record<string, unknown> | null => {
  const $pos = state.doc.resolve(pos);
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const block = $pos.node(depth);
    if (block.type.name === "paragraph") return visualParaAttrs(block);
  }
  return null;
};

/**
 * Paste companion to dropPlugin: re-apply the destination paragraph's marks so
 * pasted text inherits the template's font/size instead of the schema default
 * (Calibri 11 / "Normal text"), which otherwise produced a two-style paragraph
 * — e.g. the Code of Conduct copy button → Ctrl+V.
 *
 * SCOPE: only handles clipboards that carry NO usable `text/html`. The CoC copy
 * button writes plain text only, so it lands here; a rich paste from Word or
 * another editor still has `text/html` and falls through to docx-editor's
 * native paste, which preserves bold/italic/lists and real paragraph structure.
 * Returning `false` for those (and for image payloads) keeps that path intact.
 *
 * Blank-line-separated blocks (`\n\n`) become separate paragraph nodes so a
 * multi-paragraph declaration doesn't collapse into one; single `\n` within a
 * block stays a hardBreak (buildInlineNodes).
 */
export const pastePlugin = new Plugin({
  props: {
    handlePaste(view, event) {
      const dt = event.clipboardData;
      if (!dt) return false;
      const html = dt.getData("text/html");
      // Rich content is docx-editor's job — only take over plain-text pastes.
      if (html.trim()) return false;
      const text = dt.getData("text/plain");
      if (!text.trim()) return false;

      const { schema } = view.state;
      const paragraphType = schema.nodes.paragraph;
      if (!paragraphType) return false;

      const { from, to } = view.state.selection;
      const donorMarks = donorMarksAt(view.state, from);
      const donorAttrs = donorParaAttrs(view.state, from) ?? undefined;

      // Split on blank lines into paragraphs; each paragraph keeps soft line
      // breaks as hardBreaks. Drops empty blocks from runs of 3+ newlines.
      const blocks = text
        .split(/\n{2,}/)
        .map((b) => b.replace(/\n+$/, ""))
        .filter((b) => b.trim().length > 0);
      if (blocks.length === 0) return false;

      const paragraphs = blocks.map((block) =>
        paragraphType.create(donorAttrs, buildInlineNodes(schema, block, donorMarks))
      );

      // Open the slice one level at both ends so the first/last pasted
      // paragraphs merge into the paragraph the caret sits in (standard paste
      // behaviour) rather than splitting it into empty fragments.
      const slice = new Slice(Fragment.from(paragraphs), 1, 1);
      const tr = view.state.tr.replaceRange(from, to, slice);
      view.dispatch(tr.scrollIntoView());
      event.preventDefault();
      return true;
    },
  },
});
